import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser, faCheck } from "@fortawesome/free-solid-svg-icons";
import CommonModal from "../../../../common/commonmodal/CommonModal";
import { toast } from "react-toastify";

const ExtendVisitDurationModal = ({ isOpen, onClose, visitor }) => {
    const [extensionDuration, setExtensionDuration] = useState(60);
    const [extensionReason, setExtensionReason] = useState("Meeting Extended");
    const [customCheckoutTime, setCustomCheckoutTime] = useState("");

    const formatTimeOnly = (isoString) => {
        if (!isoString) return "—";
        const d = new Date(isoString);
        if (isNaN(d.getTime())) return "—";
        return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true });
    };

    useEffect(() => {
        if (visitor) {
            const baseCheckout = visitor.expectedEntryTime ? new Date(visitor.expectedEntryTime) : new Date();
            const newCheckout = new Date(baseCheckout.getTime() + extensionDuration * 60000);
            setCustomCheckoutTime(newCheckout.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true }));
        }
    }, [visitor, extensionDuration]);

    const handleConfirmExtension = () => {
        toast.success(`Visit duration extended successfully for ${visitor?.visitorName}`);
        onClose();
    };

    return (
        <CommonModal
            isOpen={isOpen}
            onClose={onClose}
            title="Extend Visit Duration"
            subTitle="Modify the scheduled checkout time for this visitor."
        >
            {visitor && (
                <div className="space-y-6 pt-2">
                    {/* Nested Visitor card */}
                    <div className="border border-slate-100 rounded-2xl p-4 bg-slate-50/50 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="relative">
                                {visitor.image ? (
                                    <img src={visitor.image} className="w-16 h-16 rounded-xl object-cover border border-slate-100" alt="visitor" />
                                ) : (
                                    <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400">
                                        <FontAwesomeIcon icon={faUser} className="text-2xl" />
                                    </div>
                                )}
                                <div className="absolute -bottom-1.5 -right-1.5 bg-[#22C55E] text-white w-5 h-5 rounded-full flex items-center justify-center border-2 border-white text-[10px] shadow-sm">
                                    <FontAwesomeIcon icon={faCheck} />
                                </div>
                            </div>
                            <div className="flex flex-col gap-0.5">
                                <h4 className="font-bold text-sm text-[#1E293B]">{visitor.visitorName}</h4>
                                <span className="text-[10px] font-bold text-slate-400">Visitor ID: #GKP-{visitor.visitorId || "90234"}</span>
                                <div className="flex items-center gap-2 mt-1">
                                    <span className="bg-blue-50 text-blue-600 text-[9px] font-bold px-2 py-0.5 rounded uppercase">
                                        {visitor.purposeOfVisit || "MEETING"}
                                    </span>
                                    <span className="text-slate-500 text-[10px] flex items-center gap-1 font-medium font-bold">
                                        <svg className="w-3.5 h-3.5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                        Unit: {visitor.siteName || visitor.unitName || "Zentechinfo Solution"}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div className="flex flex-col items-end gap-1.5">
                            <span className="text-[10px] text-slate-400 font-bold">Arrived: {formatTimeOnly(visitor.actualEntryTime || visitor.createdDate)}</span>
                            <span className="bg-red-50 text-[#E31F26] text-[10px] font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Current Checkout: {formatTimeOnly(visitor.expectedEntryTime)}
                            </span>
                        </div>
                    </div>

                    {/* Quick Add Duration */}
                    <div className="space-y-2">
                        <label className="text-xs font-bold text-slate-700">Quick Add Duration</label>
                        <div className="grid grid-cols-4 gap-3">
                            {[
                                { label: "+15m", mins: 15 },
                                { label: "+30m", mins: 30 },
                                { label: "+1h", mins: 60 },
                                { label: "+2h", mins: 120 }
                            ].map((d) => {
                                const isSelected = extensionDuration === d.mins;
                                return (
                                    <button
                                        key={d.label}
                                        type="button"
                                        onClick={() => setExtensionDuration(d.mins)}
                                        className={`py-2 rounded-xl text-xs font-bold border transition-all text-center
                                            ${isSelected
                                                ? "bg-[#E31F26] text-white border-[#E31F26] shadow-sm"
                                                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                                            }`}
                                    >
                                        {d.label}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    {/* Custom Checkout Time & Reason */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-700">Custom Checkout Time</label>
                            <div className="relative flex items-center border border-slate-200 rounded-xl px-3.5 py-2.5 bg-white">
                                <svg className="w-4 h-4 text-slate-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <input
                                    type="text"
                                    value={customCheckoutTime}
                                    readOnly
                                    className="bg-transparent border-none outline-none text-slate-700 text-xs font-bold w-full cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <label className="text-xs font-bold text-slate-700">Reason for Extension</label>
                            <select
                                value={extensionReason}
                                onChange={(e) => setExtensionReason(e.target.value)}
                                className="w-full border border-slate-200 rounded-xl px-3.5 py-2.5 bg-white text-slate-600 font-medium outline-none text-xs cursor-pointer"
                            >
                                <option value="Meeting Extended">Meeting Extended</option>
                                <option value="Work Delayed">Work Delayed</option>
                                <option value="Personal Reason">Personal Reason</option>
                                <option value="Other">Other</option>
                            </select>
                        </div>
                    </div>

                    {/* Projected Timeline Alert Banner */}
                    <div className="border-l-4 border-[#3B82F6] rounded-r-2xl bg-blue-50/50 p-4 flex items-center justify-between gap-4">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center text-[#3B82F6]">
                                <svg className="w-5 h-5 animate-spin" style={{ animationDuration: '3s' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 1121.21 8H18.2" />
                                </svg>
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] font-black text-blue-600 tracking-wider uppercase">Projected Timeline</span>
                                <p className="text-xs text-slate-600 font-medium mt-0.5">
                                    Extension results in a <strong className="text-blue-600 font-black">{extensionDuration} minute</strong> increase.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col items-end">
                            <span className="text-[9px] font-bold text-slate-400">New Checkout</span>
                            <strong className="text-xs text-slate-800 font-black mt-0.5">{customCheckoutTime}</strong>
                        </div>
                    </div>

                    {/* Modal Footer Buttons */}
                    <div className="flex justify-end gap-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-6 py-2.5 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold hover:bg-slate-50 transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            onClick={handleConfirmExtension}
                            className="px-6 py-2.5 rounded-xl text-white text-xs font-bold transition-colors"
                            style={{ backgroundColor: "#E31F26" }}
                        >
                            Confirm Extension
                        </button>
                    </div>
                </div>
            )}
        </CommonModal>
    );
};

export default ExtendVisitDurationModal;
